import { z } from 'zod';

export const FETCH_VISIT_LIMIT = 2;

const KEY_PREFIX = 'narvar:rm:fetch:';

// Deliberately not keyed by order: "no thanks" is an answer about pet
// insurance, not about one parcel, so it holds for every order this browser
// tracks. localStorage is per-origin and retailers do not share one -- jcpenney
// serves from its own subdomain -- so this is as wide as the browser can hold.
const DECLINED_KEY = `${KEY_PREFIX}declined`;

// A record we cannot read whole is not a record we can cap on, so a partial or
// mistyped one reads as a clean slate and the shopper keeps the offer.
const FetchOrderHistorySchema = z.object({
  visits: z.number().int().nonnegative(),
  engaged: z.boolean(),
  handedOff: z.boolean(),
});

// What is stored against the order. The decline is kept out of it so records
// written before the decline existed still parse whole, rather than reading as
// a clean slate and handing every shopper their visits back.
export type FetchOrderRecord = z.infer<typeof FetchOrderHistorySchema>;

// The order's record plus the browser-wide decline, which is what eligibility
// is actually decided on.
export type FetchOrderHistory = FetchOrderRecord & { declined: boolean };

export const NO_FETCH_HISTORY: FetchOrderHistory = {
  visits: 0,
  engaged: false,
  handedOff: false,
  declined: false,
};

const store = (): Storage | undefined => {
  if (typeof window === 'undefined') return undefined;
  try {
    // reading the localStorage property can throw if we're in an iframe
    return window.localStorage;
  } catch (err) {
    console.error('Error reading localStorage', err);
    return undefined;
  }
};

export function fetchOrderKey(
  orderNumbers?: string[],
  trackingNumbers?: string[],
): string | undefined {
  return orderNumbers?.[0] ?? trackingNumbers?.[0];
}

export function readFetchDeclined(): boolean {
  const storage = store();
  if (!storage) return false;
  try {
    return storage.getItem(DECLINED_KEY) === 'true';
  } catch (err) {
    console.error('Error reading Fetch decline', err);
    return false;
  }
}

export function markFetchDeclined(): void {
  const storage = store();
  if (!storage) return;
  try {
    storage.setItem(DECLINED_KEY, 'true');
  } catch (err) {
    console.error('Error writing Fetch decline', err);
  }
}

function readFetchOrderRecord(orderKey: string): FetchOrderRecord {
  const storage = store();
  if (!storage) return NO_FETCH_HISTORY;
  try {
    const raw = storage.getItem(`${KEY_PREFIX}${orderKey}`);
    if (!raw) return NO_FETCH_HISTORY;
    const parsed = FetchOrderHistorySchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : NO_FETCH_HISTORY;
  } catch (err) {
    console.error('Error reading Fetch history', err);
    return NO_FETCH_HISTORY;
  }
}

export function readFetchHistory(orderKey: string): FetchOrderHistory {
  return { ...readFetchOrderRecord(orderKey), declined: readFetchDeclined() };
}

export function writeFetchHistory(
  orderKey: string,
  { visits, engaged, handedOff }: FetchOrderRecord,
): void {
  const storage = store();
  if (!storage) return;
  try {
    storage.setItem(
      `${KEY_PREFIX}${orderKey}`,
      JSON.stringify({ visits, engaged, handedOff }),
    );
  } catch (err) {
    console.error('Error writing Fetch history', err);
  }
}

function amendFetchHistory(
  orderKey: string | undefined,
  amendment: Partial<FetchOrderRecord>,
): void {
  if (!orderKey) return;
  writeFetchHistory(orderKey, { ...readFetchHistory(orderKey), ...amendment });
}

export function markFetchEngaged(orderKey: string | undefined): void {
  amendFetchHistory(orderKey, { engaged: true });
}

export function markFetchPurchased(orderKey: string | undefined): void {
  amendFetchHistory(orderKey, { handedOff: true });
}

export function isFetchEligible(history: FetchOrderHistory): boolean {
  if (history.declined || history.handedOff) return false;
  return history.engaged || history.visits < FETCH_VISIT_LIMIT;
}
